import { View } from '@play-co/timestep-core/ui';
import PopupBasic from '../../components/popups/PopupBasic';
import { Feature } from 'src/replicant/analytics';
import {
  PaymentPopupResult,
  PaymentMethodResult,
  WebPaymentMethod,
} from '../iap.types';
import { WebPaymentMethodAnalytics } from '../analytics/analytics.types';
import { h, render } from 'preact';
import { PopupOpenWebPayView } from '../components/PopupOpenWebPayView';
import { ProductId } from 'src/replicant/iap';
import {
  trackWebPayAgeConfirm,
  trackWebPayCloseCancel,
  trackWebPayCloseStart,
  trackWebPayMethodCancel,
  trackWebPayMethodSelect,
  trackWebPayPurchaseStart,
} from '../analytics/iap.web.pay';
import { captureGenericError } from 'src/lib/sentry';
import { now } from 'src/StateObserver';
import { saveLastSelectedPaymentMethod } from '../iap.utils';
import { getKomojuSessionCache } from '../providers/komoju';
import {
  createPaymentPopupAnalytics,
  createPaymentPopupResult,
  trackPopupOpenWebPayShow,
} from './analytics';

import { AssetGroup } from '@play-co/timestep-core';

type CloseHandler = (result: PaymentPopupResult) => void;

/**
 * Popup for open web payment.
 * The view is actually rendered by preact. This class is the bridge between the game and the view.
 */
export default class PopupOpenWebPay extends PopupBasic {
  private close: CloseHandler;
  private popup: HTMLElement | null = null;

  private openTimestamp!: number;
  private productId!: ProductId;
  private paymentMethod?: WebPaymentMethod;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () =>
        this.close(
          this.compileResult({
            paymentResult: {
              success: false,
              code: 'CANCELED',
              provider: 'none',
              purchaseID: 'unknown',
              internalTransactionId: 'unknown',
            },
            analyticsData: {
              instanceSelectSequence: 0,
            },
          }),
        ),
      darkerBg: true,
      skipMessage: true,
      skipTitle: true,
      tracking: {
        feature: Feature.OPENWEB._,
        $subFeature: Feature.OPENWEB.PAYMENT._,
      },
      zIndex: 10000,
    });

    // Hide some default popup UI
    this.buttonClose.hide();
    this.box.hide();

    // Save close handler.
    this.close = (args) => {
      this.dispose();

      // Log error if there is any.
      if (!args.success && 'error' in args) {
        captureGenericError('Payment dialog closed with error', args.error);
        console.error(args.error);
      }

      // Store the successfull payment method, if the feature is enabled
      if (args.success && this.paymentMethod) {
        saveLastSelectedPaymentMethod(this.paymentMethod);
      }

      return opts.close(args);
    };
  }

  protected getAssetsGroup() {
    return new AssetGroup([]);
  }

  public async init(opts: { productId: ProductId }) {
    super.init({});

    // Invalidate session cache for the product to get a fresh session.
    getKomojuSessionCache(opts.productId).invalidate();

    this.openTimestamp = now();
    this.productId = opts.productId;

    // Track the open dialog event
    await trackPopupOpenWebPayShow(this.productId);

    try {
      // Create the UI.
      const div = document.createElement('div');
      document.body.appendChild(div);
      render(
        h(PopupOpenWebPayView, {
          ...opts,
          close: (result) => this.close(this.compileResult(result)),
          onMethodSelected: (
            data: WebPaymentMethodAnalytics,
            isCancel: boolean,
          ) => {
            this.paymentMethod = data.methodData?.providerMethod;
            const analytics = this.compileAnalytics(data);

            if (isCancel) {
              trackWebPayMethodCancel({
                ...analytics.analytics,
                ...analytics.methodAnalytics!,
              });
            } else {
              trackWebPayMethodSelect({
                ...analytics.analytics,
                ...analytics.methodAnalytics!,
              });
            }
          },
          onPurchaseStart: (data) => {
            const analytics = this.compileAnalytics(data);
            trackWebPayPurchaseStart({
              ...analytics.analytics,
              ...analytics.methodAnalytics!,
            });
          },
          onAgeConfirm: (data) => {
            const analytics = this.compileAnalytics(data);
            trackWebPayAgeConfirm({
              ...analytics.analytics,
              ...analytics.methodAnalytics!,
              accepted: data.accepted,
            });
          },
          onCloseStart: (data) => {
            const analytics = this.compileAnalytics(data);
            trackWebPayCloseStart({
              ...analytics.analytics,
              ...analytics.methodAnalytics!,
            });
          },
          onCloseCancel: (data) => {
            const analytics = this.compileAnalytics(data);
            trackWebPayCloseCancel({
              ...analytics.analytics,
              ...analytics.methodAnalytics!,
            });
          },
        }),
        div,
      );
      this.popup = div;
    } catch (e: any) {
      this.close(
        this.compileResult({
          paymentResult: {
            success: false,
            provider: 'none',
            code: 'SERVICE_ERROR',
            error: e,
            purchaseID: 'unknown',
            internalTransactionId: 'unknown',
          },
          analyticsData: {
            instanceSelectSequence: 0,
          },
        }),
      );
    }
  }

  /**
   * Disposes of the popup.
   */
  public dispose() {
    if (!this.popup) return;
    this.popup.remove();
    this.popup = null;
  }

  /**
   * Compiles the analytics data for a given payment method.
   *
   * @param methodResult - The result of the payment method.
   * @returns The compiled payment popup result.
   */
  private compileAnalytics(data: WebPaymentMethodAnalytics) {
    return createPaymentPopupAnalytics(data, {
      productId: this.productId,
      openTimestamp: this.openTimestamp,
    });
  }

  /**
   * Compiles the result of a payment method and creates a payment popup result.
   *
   * @param methodResult - The result of the payment method.
   * @returns The compiled payment popup result.
   */
  private compileResult(methodResult: PaymentMethodResult): PaymentPopupResult {
    return createPaymentPopupResult(methodResult, {
      productId: this.productId,
      openTimestamp: this.openTimestamp,
    });
  }
}
